/**
 * Infrastructure errors are normalised before they reach the UI: a Postgres or GoTrue
 * message is never shown to a family member (react-pwa-engineering skill 27).
 */
export type AppError =
  | { kind: 'validation'; message: string }
  | { kind: 'unauthorized'; message: string }
  | { kind: 'network'; message: string; retryable: boolean }
  | { kind: 'unknown'; message: string };

/** RPC error codes raised by the database (agents-doc/plans/README.md 3). */
const RPC_MESSAGES: Record<string, string> = {
  NOT_MEMBER: 'Bạn không có quyền với dữ liệu này.',
  MONTH_CLOSED: 'Tháng này đã chốt sổ nên không sửa được. Mở lại ở Việc hàng tháng rồi thử lại.',
  UNBALANCED_ENTRY: 'Số tiền hai bên chưa khớp nhau.',
  INVALID_SHAPE: 'Giao dịch chưa hợp lệ.',
  CURRENCY_NOT_VND: 'Hiện chỉ hỗ trợ tiền VND.',
  ALREADY_CONFIRMED: 'Mục này đã được xác nhận trước đó.',
  DUPLICATE_IMPORT: 'Dữ liệu này đã được nhập rồi.',
  ACCOUNT_NOT_EMPTY:
    'Tài khoản vẫn còn số dư nên chưa đóng được. Hãy chuyển hết tiền đi (hoặc trả hết nợ) rồi đóng lại.',
  ALREADY_HAS_HOUSEHOLD: 'Tài khoản này đã có không gian tài chính.',
  ALREADY_FAMILY: 'Không gian này đã là gia đình.',
  USER_NOT_FOUND: 'Không tìm thấy người dùng với email này.',
  USER_ALREADY_IN_HOUSEHOLD: 'Người này đã thuộc một gia đình khác.',
  MEMBER_NOT_FOUND: 'Không tìm thấy thành viên này trong gia đình.',
  CANNOT_REMOVE_SELF: 'Bạn không tự xoá mình khỏi gia đình được.',
  LAST_OWNER: 'Gia đình phải luôn có ít nhất một chủ hộ.',
  TEMPLATE_INACTIVE: 'Khoản định kỳ này đang tắt.',
  AMOUNT_REQUIRED: 'Cần nhập số tiền thực tế.',
  OUT_OF_ORDER: 'Kỳ sau đã được ghi rồi nên không lùi lại kỳ này được. Hãy chọn "Bỏ luôn kỳ này".',
};

/**
 * GoTrue error codes (`AuthApiError.code`), for the password change on the profile screen.
 * Matched by code rather than sentence because the code is what stays stable across GoTrue
 * releases. The exact strength rule is server configuration, so the hint stays general.
 */
const AUTH_CODE_MESSAGES: Record<string, string> = {
  same_password: 'Mật khẩu mới phải khác mật khẩu hiện tại.',
  weak_password: 'Mật khẩu mới chưa đủ mạnh. Hãy dùng mật khẩu dài hơn, có cả chữ và số.',
};

const AUTH_MESSAGES: Record<string, string> = {
  'Invalid login credentials': 'Email hoặc mật khẩu chưa đúng.',
  'Email logins are disabled': 'Đăng nhập bằng email đang bị tắt.',
};

function messageOf(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    const { message } = error as { message?: unknown };
    if (typeof message === 'string') return message;
  }
  return '';
}

function codeOf(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const { code } = error as { code?: unknown };
    if (typeof code === 'string') return code;
  }
  return '';
}

/** Turns anything thrown by Supabase into something a person can act on. */
export function toAppError(error: unknown): AppError {
  // Repositories normalise before they throw, and screens normalise what they catch. Without
  // this, the second pass would look for an error code inside an already-translated sentence,
  // find none, and replace a specific message with the generic one.
  if (error instanceof AppErrorException) {
    return error.appError;
  }

  const raw = messageOf(error);

  for (const [code, message] of Object.entries(RPC_MESSAGES)) {
    if (raw.includes(code)) {
      return { kind: 'validation', message };
    }
  }

  const code = codeOf(error);
  if (AUTH_CODE_MESSAGES[code]) {
    return { kind: 'validation', message: AUTH_CODE_MESSAGES[code] };
  }

  if (AUTH_MESSAGES[raw]) {
    return { kind: 'unauthorized', message: AUTH_MESSAGES[raw] };
  }

  if (/fetch|network|timeout|Failed to fetch/i.test(raw)) {
    return {
      kind: 'network',
      message: 'Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.',
      retryable: true,
    };
  }

  if (!raw) {
    return { kind: 'unknown', message: 'Có lỗi xảy ra. Vui lòng thử lại.' };
  }

  return { kind: 'unknown', message: 'Có lỗi xảy ra. Vui lòng thử lại.' };
}

/** A real Error so stack traces and `instanceof` keep working across await boundaries. */
export class AppErrorException extends Error {
  readonly appError: AppError;

  constructor(appError: AppError) {
    super(appError.message);
    this.name = 'AppErrorException';
    this.appError = appError;
  }
}

export function appErrorException(error: unknown): AppErrorException {
  return new AppErrorException(toAppError(error));
}
